/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: ARSPage.tsx (ARS - Route Selections)
 🗓️ Created: 2025-01-15
 🧩 Description:
    ARS (Automatic Route Selection) list page with table view, search,
    pagination, and actions.

 🔧 Notes:
    - Refactored from monolithic file
    - Logic delegated to hooks/
    - API calls moved to api/
    - JSX rendering for columns moved here from hook

 🔁 Last Updated: 2025-01-15 by Rodrigo Cuadra (Refactored)
────────────────────────────────────────────────────────────────*/

import { useState, useCallback, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useI18n } from '../../hooks/useI18n';
import { useNavigationStore } from '../../store/navigation';
import { useNotifications } from '../../contexts/NotificationContext';
import DataGridSimple from '../../components/ui/DataGridSimple';
import ConfirmationModal from '../../components/ui/ConfirmationModal';
import { Edit, Trash2, Copy, Route } from 'lucide-react';
import SimpleTooltip from '../../components/ui/SimpleTooltip';
import { ModulePageContainer } from '../../components/layouts';
import { useArsList } from './hooks';
import { deleteArsProfileById } from './api/arsApi';
import type { ArsProfile } from './types';

const EMPTY_PLACEHOLDER = '—';
const ACTION_BUTTON_STYLES = {
  edit: 'p-1 rounded transition-colors text-blue-600 hover:text-blue-700 hover:bg-blue-100/60 dark:text-blue-300 dark:hover:text-blue-200 dark:hover:bg-blue-900/30',
  duplicate: 'p-1 rounded transition-colors text-green-600 hover:text-green-700 hover:bg-green-100/60 dark:text-green-300 dark:hover:text-green-200 dark:hover:bg-green-900/30',
  delete: 'p-1 rounded transition-colors text-red-600 hover:text-red-700 hover:bg-red-100/60 dark:text-red-300 dark:hover:text-red-200 dark:hover:bg-red-900/30'
} as const;

export default function ARSPage() {
  const { t } = useI18n();
  const { setCurrentPageTitle } = useNavigationStore();
  const navigate = useNavigate();
  const { showSuccess, showError } = useNotifications();
  const pageTitle = useMemo(() => t('ars.title'), [t]);
  const dateFormatter = useMemo(() => new Intl.DateTimeFormat(undefined), []);

  // Set page title
  useEffect(() => {
    setCurrentPageTitle(null);
    return () => setCurrentPageTitle(null);
  }, [setCurrentPageTitle]);

  // Delete confirmation state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [profileToDelete, setProfileToDelete] = useState<ArsProfile | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const {
    data,
    loading,
    columns: baseColumns,
    pagination,
    searchQuery,
    sortBy,
    sortOrder,
    handleSearch,
    handleSort,
    handlePageChange,
    handleItemsPerPageChange,
    handleColumnToggle,
    refresh
  } = useArsList();

  // Add render functions to columns (JSX must be in .tsx file)
  const columns = useMemo(() => {
    return baseColumns.map((column) => {
      if (column.key === 'name') {
        return {
          ...column,
          render: (_value: unknown, item: ArsProfile) => (
            <span className="font-medium text-foreground">{item.name}</span>
          )
        };
      }

      if (column.key === 'description') {
        return {
          ...column,
          render: (_value: unknown, item: ArsProfile) => (
            <span className="text-muted-foreground max-w-md truncate block">
              {item.description?.trim() || EMPTY_PLACEHOLDER}
            </span>
          )
        };
      }

      if (column.key === 'routes') {
        return {
          ...column,
          render: (_value: unknown, item: ArsProfile) => (
            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300">
              {item.route_count ?? 0} {t('ars.routeCount')}
            </span>
          )
        };
      }

      if (column.key === 'enabled') {
        return {
          ...column,
          render: (_value: unknown, item: ArsProfile) => (
            item.enabled
              ? (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300">
                  {t('common.enabled')}
                </span>
              )
              : (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200">
                  {t('common.disabled')}
                </span>
              )
          )
        };
      }

      if (column.key === 'createdAt') {
        return {
          ...column,
          render: (_value: unknown, item: ArsProfile) => {
            const date = new Date(item.createdAt);
            return (
              <span className="text-sm text-muted-foreground">
                {!Number.isNaN(date.getTime()) ? dateFormatter.format(date) : EMPTY_PLACEHOLDER}
              </span>
            );
          }
        };
      }

      if (column.key === 'actions') {
        return {
          ...column,
          render: (_value: unknown, item: ArsProfile) => renderActions(item)
        };
      }

      return column;
    });
  }, [baseColumns, t, dateFormatter]);

  // Handlers
  const handleCreate = useCallback(() => {
    navigate('/pbx/class-of-service/route-selections/new');
  }, [navigate]);

  const handleEdit = useCallback((profile: ArsProfile) => {
    navigate(`/pbx/class-of-service/route-selections/${profile.uuid || profile.id}`);
  }, [navigate]);

  const handleDuplicate = useCallback((profile: ArsProfile) => {
    navigate(`/pbx/class-of-service/route-selections/new?duplicate=${profile.uuid || profile.id}`);
  }, [navigate]);

  const handleDelete = useCallback((profile: ArsProfile) => {
    setProfileToDelete(profile);
    setShowDeleteModal(true);
  }, []);

  const confirmDelete = useCallback(async () => {
    if (!profileToDelete) return;

    setIsDeleting(true);
    try {
      await deleteArsProfileById(profileToDelete.id);

      showSuccess(t('ars.messages.deleteSuccess'));
      refresh();
      setShowDeleteModal(false);
      setProfileToDelete(null);
    } catch (error) {
      const message = error instanceof Error ? error.message : t('ars.messages.deleteError');
      showError(message);
    } finally {
      setIsDeleting(false);
    }
  }, [profileToDelete, refresh, showError, showSuccess, t]);

  // Render actions column
  const renderActions = useMemo(() => (profile: ArsProfile) => (
    <div className="flex items-center gap-2">
      <SimpleTooltip content={t('common.edit')}>
        <button
          onClick={() => handleEdit(profile)}
          className={ACTION_BUTTON_STYLES.edit}
        >
          <Edit className="h-4 w-4" />
        </button>
      </SimpleTooltip>

      <SimpleTooltip content={t('common.duplicate')}>
        <button
          onClick={() => handleDuplicate(profile)}
          className={ACTION_BUTTON_STYLES.duplicate}
        >
          <Copy className="h-4 w-4" />
        </button>
      </SimpleTooltip>
      
      <SimpleTooltip content={t('common.delete')}>
        <button
          onClick={() => handleDelete(profile)}
          className={ACTION_BUTTON_STYLES.delete}
        >
          <Trash2 className="h-4 w-4" />
        </button>
      </SimpleTooltip>
    </div>
  ), [handleDelete, handleDuplicate, handleEdit, t]);

  return (
    <ModulePageContainer
      currentView="list"
      listView={
        <DataGridSimple
          data={data}
          columns={columns}
          onColumnToggle={handleColumnToggle}
          loading={loading}
          pagination={pagination}
          onPageChange={handlePageChange}
          onItemsPerPageChange={handleItemsPerPageChange}
          searchQuery={searchQuery}
          onSearch={handleSearch}
          sortBy={sortBy}
          sortOrder={sortOrder}
          onSort={handleSort}
          onAdd={handleCreate}
          onDuplicate={handleDuplicate}
          title={t('ars.title')}
          titleIcon={<Route className="h-5 w-5 text-primary" />}
          addButtonText={t('ars.actions.create')}
          searchPlaceholder={t('ars.searchPlaceholder')}
          renderActions={renderActions}
          emptyMessage={t('ars.empty.description')}
        />
      }
      formView={null}
    >
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={confirmDelete}
        title={t('ars.messages.confirmDeleteTitle')}
        message={t('ars.messages.confirmDeleteMessage', { name: profileToDelete?.name ?? '' })}
        confirmText={t('common.delete')}
        cancelText={t('common.cancel')}
        isLoading={isDeleting}
      />
    </ModulePageContainer>
  );
}
